import Vue from "vue"
import Vuex from "vuex"
import Axios from "axios"
import dayjs from "dayjs"
import router from "../router"
import {
  sortExhibitions,
  sortPublications,
  filterAreas,
  separateArea,
} from "@/utils"
import { uniqBy } from "lodash"

const API = Axios.create({
  baseURL: process.env.VUE_APP_FA_API_ROOT,
  withCredentials: true,
})

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    loading: true,
    appTitle: process.env.VUE_APP_TITLE,
    site: process.env.VUE_APP_SITE,
    currentEvent: null,
    currentExhibition: null,
    currentInvestigation: false,
    currentVideo: false,
    events: null,
    exhibitions: null,
    fapress: [],
    investigationCategories: null,
    investigationLocations: null,
    investigationMethodologies: null,
    investigations: null,
    mapLocations: null,
    methodologies: null,
    news: null,
    pages: {},
    pageTitle: false,
    publications: null,
    recognition: null,
    searchInProgress: false,
    searchQuery: "",
    searchResults: [],
    showAllCurrent: false,
    showAllExhibitions: false,
    showInvestigationSubSectionText: false,
    showMap: false,
    showSearch: false,
    showSitemap: false,
    studies: null,
    subdomains: null,
    team: null,
    ticker: null,
  },
  mutations: {
    ADD_INVESTIGATION(state, payload) {
      state.investigations.push(payload)
    },
    START_LOADING(state) {
      state.loading = true
    },
    STOP_LOADING(state) {
      state.loading = false
    },
    SHOW_ALL_CURRENT(state) {
      state.showAllCurrent = true
    },
    SHOW_ALL_EXHIBITIONS(state) {
      state.showAllExhibitions = true
    },
    UPDATE_RECOGNITION(state, payload) {
      state.recognition = payload
    },
    UPDATE_TEAM(state, payload) {
      state.team = payload
    },
    UPDATE_CURRENT_VIDEO(state, payload) {
      state.currentVideo = payload
    },
    UPDATE_EXHIBITIONS(state, payload) {
      state.exhibitions = payload
    },
    UPDATE_EVENTS(state, payload) {
      state.events = payload
    },
    UPDATE_FA_PRESS(state, payload) {
      state.fapress = payload
    },
    UPDATE_INVESTIGATIONS(state, payload) {
      state.investigations = payload
    },
    UPDATE_STUDIES(state, payload) {
      state.studies = payload
    },
    UPDATE_INVESTIGATION_CATEGORIES(state, payload) {
      state.investigationCategories = payload
    },
    UPDATE_INVESTIGATION_LOCATIONS(state, payload) {
      state.investigationLocations = payload
    },
    UPDATE_INVESTIGATION_METHODOLOGIES(state, payload) {
      state.investigationMethodologies = payload
    },
    UPDATE_NEWS(state, payload) {
      state.news = payload
    },
    UPDATE_TICKER(state, payload) {
      state.ticker = payload
    },
    UPDATE_EVENT_ACF(state, payload) {
      let event = state.events.find((event) => event.id === payload.id)
      Vue.set(event, "acf", payload.data)
    },
    UPDATE_EXHIBITION_ACF(state, payload) {
      let exhibition = state.exhibitions.find((exh) => exh.id === payload.id)
      Vue.set(exhibition, "acf", payload.data)
    },
    UPDATE_INVESTIGATION_ACF(state, payload) {
      let investigation = this.getters.allInvestigations.find(
        (inv) => inv.id === payload.id
      )
      Vue.set(investigation, "acf", payload.data)
    },
    UPDATE_MAP_LOCATIONS(state, payload) {
      state.mapLocations = payload
    },
    UPDATE_METHODOLOGIES(state, payload) {
      state.methodologies = payload
    },
    UPDATE_PAGE_TITLE(state, payload) {
      state.pageTitle = payload
    },
    UPDATE_PAGES(state, payload) {
      let slug = payload[0].slug
      state.pages[slug] = payload[0]
    },
    UPDATE_PUBLICATIONS(state, payload) {
      state.publications = payload
    },
    UPDATE_SHOW_SEARCH(state, payload) {
      if (payload === "hide") {
        state.showSearch = false
      } else {
        state.showSearch = !state.showSearch
      }
    },
    UPDATE_SEARCH_QUERY(state, payload) {
      state.searchQuery = payload
    },
    UPDATE_SEARCH_RESULTS(state, payload) {
      state.searchResults = payload
    },
    UPDATE_SHOWINVESTIGATIONSUBSECTIONTEXT(state, payload) {
      state.showInvestigationSubSectionText = payload
    },
    UPDATE_SITEMAP(state, payload) {
      if (payload === "hide") {
        state.showSitemap = false
      } else {
        state.showSitemap = !state.showSitemap
      }
    },
    UPDATE_SUBDOMAINS(state, payload) {
      state.subdomains = payload
    },
    SET_CURRENT_EXHIBITION(state, payload) {
      state.currentExhibition = payload
    },
    SET_CURRENT_INVESTIGATION(state, payload) {
      state.currentInvestigation = payload
    },
    SET_CURRENT_EVENT(state, payload) {
      state.currentEvent = payload
    },
  },
  actions: {
    showExhibitions: function (context) {
      context.commit("SHOW_ALL_EXHIBITIONS", true)
    },
    showCurrent: function (context) {
      context.commit("SHOW_ALL_CURRENT", true)
    },
    updateCurrentVideo: function (context, payload) {
      context.commit("UPDATE_CURRENT_VIDEO", payload)
    },
    updateInvestigationFilters: function (context) {
      let locations = []
      let methodologies = []

      this.state.investigations.forEach(function (investigation) {
        investigation.investigation_location.map((x) => {
          locations.push({
            id: x.term_id,
            name: x.name,
            slug: x.slug,
          })
        })

        if (investigation.methodologies) {
          investigation.methodologies.map((x) => {
            methodologies.push({
              id: x.ID,
              name: x.post_title,
              slug: x.post_name,
            })
          })
        }
      })

      const locations_unique = []
      const mapLocations = new Map()
      for (const item of locations) {
        if (!mapLocations.has(item.id)) {
          mapLocations.set(item.id, true)
          locations_unique.push({
            id: item.id,
            name: item.name,
            slug: item.slug,
          })
        }
      }

      const locations_unique_sorted = locations_unique.sort((a, b) => {
        if (a.slug < b.slug) {
          return -1
        } else {
          return 1
        }
      })

      const methodologies_unique = []
      const mapMethodologies = new Map()
      for (const item of methodologies) {
        if (!mapMethodologies.has(item.id)) {
          mapMethodologies.set(item.id, true)
          methodologies_unique.push({
            id: item.id,
            name: item.name,
            slug: item.slug,
          })
        }
      }

      const methodologies_unique_sorted = methodologies_unique.sort((a, b) => {
        if (a.slug < b.slug) {
          return -1
        } else {
          return 1
        }
      })

      context.commit("UPDATE_INVESTIGATION_LOCATIONS", locations_unique_sorted)
      context.commit(
        "UPDATE_INVESTIGATION_METHODOLOGIES",
        methodologies_unique_sorted
      )
    },
    getRecognition: async function (context) {
      if (this.state.recognition === null) {
        context.commit("START_LOADING")
        try {
          let response = await API.get(`fa/v1/recognition`)
          context.commit("UPDATE_RECOGNITION", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getExhibitions: async function (context) {
      if (!this.state.exhibitions) {
        context.commit("START_LOADING")
        try {
          let url = `fa/v1/exhibitions`
          if (process.env.VUE_APP_SITE) {
            url = `fa/v1/exhibitions?site=${process.env.VUE_APP_SITE}`
          }
          let response = await API.get(url)
          context.commit("UPDATE_EXHIBITIONS", response.data.exhibitions)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getExhibition: async function (context, payload) {
      let currentExhibition = context.state.exhibitions.find(
        (inv) => inv.slug === payload
      )
      if (!currentExhibition.hasOwnProperty("acf")) {
        context.commit("START_LOADING")
        Vue.set(currentExhibition, "acf", false)
        let exhibitionId = currentExhibition.id
        try {
          let response = await API.get(`acf/v3/exhibition/${exhibitionId}`)
          let payload = {
            id: exhibitionId,
            data: response.data.acf,
          }
          context.commit("UPDATE_EXHIBITION_ACF", payload)
          context.commit("SET_CURRENT_EXHIBITION", currentExhibition.id)
          context.commit("UPDATE_PAGE_TITLE", currentExhibition.title)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("SET_CURRENT_EXHIBITION", currentExhibition.id)
          return error
        }
      } else {
        context.commit("UPDATE_PAGE_TITLE", currentExhibition.title)
        return true
      }
    },
    getEvents: async function (context) {
      if (this.state.events === null) {
        context.commit("START_LOADING")
        try {
          let url = `fa/v1/events`
          if (process.env.VUE_APP_SITE) {
            url = `fa/v1/events?site=${process.env.VUE_APP_SITE}`
          }
          let response = await API.get(url)
          context.commit("UPDATE_EVENTS", response.data.events)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getEvent: async function (context, payload) {
      let currentEvent = context.state.events.find(
        (inv) => inv.slug === payload
      )
      if (!currentEvent.hasOwnProperty("acf")) {
        context.commit("START_LOADING")
        Vue.set(currentEvent, "acf", false)
        let eventId = currentEvent.id
        try {
          let response = await API.get(`acf/v3/event/${eventId}`)
          let payload = {
            id: eventId,
            data: response.data.acf,
          }
          context.commit("UPDATE_EVENT_ACF", payload)
          context.commit("SET_CURRENT_EVENT", currentEvent.id)
          context.commit("UPDATE_PAGE_TITLE", currentEvent.title)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("SET_CURRENT_Event", currentEvent.id)
          return error
        }
      } else {
        context.commit("UPDATE_PAGE_TITLE", currentEvent.title)
        return true
      }
    },
    getSubdomains: async function (context) {
      if (this.state.subdomains === null) {
        context.commit("START_LOADING")
        try {
          let response = await API.get(`wp/v2/subdomain`)
          context.commit("UPDATE_SUBDOMAINS", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getTicker: async function (context) {
      if (this.state.ticker === null) {
        try {
          let response = await API.get(
            `fa/v1/ticker?site=${process.env.VUE_APP_SITE}`
          )
          context.commit("UPDATE_TICKER", response.data)
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getInvestigations: async function (context) {
      if (this.state.investigations === null) {
        context.commit("START_LOADING")
        try {
          let url = `fa/v1/investigations`
          if (process.env.VUE_APP_SITE) {
            url = `fa/v1/investigations?site=${process.env.VUE_APP_SITE}`
          }
          let response = await API.get(url)

          const areas = filterAreas(response.data.investigation_areas, "Study")
          const [studies, investigations] = separateArea(
            response.data.investigations,
            "Study"
          )

          context.commit("UPDATE_INVESTIGATIONS", investigations)
          context.commit("UPDATE_STUDIES", studies)
          context.commit("UPDATE_INVESTIGATION_CATEGORIES", areas)
          context.dispatch("updateInvestigationFilters")
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          console.log(error)
          router.push("/maintenance")
          // context.commit('STOP_LOADING')
          return error
        }
      }
    },
    getInvestigation: async function (context, payload) {
      let currentInvestigation = context.getters.allInvestigations.find(
        (inv) => inv.slug === payload.params.investigationSlug
      )
      context.commit("START_LOADING")
      let investigationId = currentInvestigation.id

      try {
        let response = await API.get(`acf/v3/investigation/${investigationId}`)
        let payload = {
          id: investigationId,
          data: response.data.acf,
        }
        context.commit("UPDATE_INVESTIGATION_ACF", payload)
        context.commit("SET_CURRENT_INVESTIGATION", payload.id)
        context.commit("UPDATE_PAGE_TITLE", currentInvestigation.title)
        context.commit("STOP_LOADING")
        return true
      } catch (error) {
        console.log(error)
        return error
      }
    },
    getMapLocations: async function (context) {
      if (this.state.mapLocations === null) {
        context.commit("START_LOADING")
        try {
          let response = await API.get(`fa/v1/map`)
          context.commit("UPDATE_MAP_LOCATIONS", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getMethodologies: async function (context) {
      if (this.state.methodologies === null) {
        try {
          let response = await API.get(`wp/v2/methodology?per_page=100`)
          context.commit("UPDATE_METHODOLOGIES", response.data)
          return true
        } catch (error) {
          return error
        }
      }
    },
    getNews: async function (context) {
      if (this.state.news === null) {
        context.commit("START_LOADING")
        try {
          // TODO: Pagination
          let url = `fa/v1/news?per_page=100`
          if (process.env.VUE_APP_SITE) {
            url = `fa/v1/news?site=${process.env.VUE_APP_SITE}`
          }
          let response = await API.get(url)
          context.commit("UPDATE_NEWS", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getPage: async function (context, payload) {
      if (payload === "recognition") {
        context.dispatch("getRecognition")
      }

      if (
        !Object.keys(this.state.pages).length ||
        !this.state.pages.hasOwnProperty(payload)
      ) {
        context.commit("START_LOADING")
        try {
          let url = `wp/v2/pages?path=/about/${payload}`
          if (process.env.VUE_APP_SITE) {
            url = `wp/v2/pages?path=/about-${process.env.VUE_APP_SITE}/${payload}`
          }

          let response = await API.get(url)
          context.commit("UPDATE_PAGES", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getfapress: async function (context) {
      if (this.state.fapress === null) {
        context.commit("START_LOADING")
        let url = `wp/v2/press?press_types=81&per_page=100`
        if (
          process.env.VUE_APP_SITE &&
          process.env.VUE_APP_SITE === "forensis"
        ) {
          url = `wp/v2/press?press_types=355&per_page=100`
        }
        try {
          // TODO: Maxes out at 100, add pagination
          let response = await API.get(url)
          context.commit("UPDATE_FA_PRESS", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getPublications: async function (context) {
      if (this.state.publications === null) {
        context.commit("START_LOADING")
        try {
          let url = `fa/v1/publications`
          if (process.env.VUE_APP_SITE) {
            url = `fa/v1/publications?site=${process.env.VUE_APP_SITE}`
          }
          let response = await API.get(url)
          context.commit("UPDATE_PUBLICATIONS", response.data)
          context.commit("STOP_LOADING")
          return true
        } catch (error) {
          context.commit("STOP_LOADING")
          return error
        }
      }
    },
    getTeam: function (context) {
      if (this.state.team === null) {
        API.get(`acf/v3/pages/1546`, {
          params: {
            categories: 2,
            per_page: 100,
          },
        })
          .then((response) => {
            context.commit("UPDATE_TEAM", response.data.acf)
            return true
          })
          .catch((error) => {
            return error
          })
      }
    },
    search: async function (context) {
      this.state.searchInProgress = true
      API.get(
        `fa/v1/search?q=${this.state.searchQuery}&site=${this.state.site}`
      )
        .then((response) => {
          context.commit("UPDATE_SEARCH_RESULTS", response.data)
          this.state.searchInProgress = false
        })
        .catch((error) => {
          this.state.searchInProgress = true
          return error
        })
    },
    toggleMap: function (context) {
      if (!this.state.showMap) {
        if (!this.state.mapLocations) {
          context.dispatch("getMapLocations").then(() => {
            this.state.showMap = true
          })
        } else {
          this.state.showMap = true
        }
      } else {
        this.state.showMap = false
      }
    },
    setShowInvestigationSubSectionText(context, payload) {
      context.commit("UPDATE_SHOWINVESTIGATIONSUBSECTIONTEXT", payload)
    },
  },
  getters: {
    // all investigations and studies
    allInvestigations: (state) => {
      if (state.investigations && state.studies)
        return state.investigations.concat(state.studies)
    },
    // Currently displayed investigation
    currentInvestigation: (state) => {
      if (state.investigations && state.studies) {
        return state.investigations
          .concat(state.studies)
          .find((x) => x.id === state.currentInvestigation)
      } else {
        return false
      }
    },
    // Current investigaition Description
    currentInvestigationDescription: (state, getters) => {
      if (
        getters.currentInvestigation &&
        getters.currentInvestigation.hasOwnProperty("acf")
      ) {
        return getters.currentInvestigation.acf.description
      } else {
        return false
      }
    },
    // Currently displayed exhibition
    currentExhibition: (state) => {
      if (state.exhibitions) {
        return state.exhibitions.find((x) => x.id === state.currentExhibition)
      } else {
        return false
      }
    },
    // Exhibitions 'On Now'
    // currentExhibitions: (state) => {
    //   if (state.exhibitions) {
    //     let now = dayjs(new Date())
    //     let currentExhibitions = state.exhibitions.filter(ex => {
    //       return dayjs(ex.end.date).isAfter(now)
    //     })
    //     return sortExhibitions(currentExhibitions)
    //   } else {
    //     return false
    //   }
    // },
    // Events - Current
    currentExhibitions: (state) => {
      if (state.events) {
        let now = dayjs(new Date())
        let currentExhibitions = state.events.filter((event) => {
          if (event.type) {
            return (
              event.type[0].term_id === 55 && dayjs(event.end.date).isAfter(now)
            )
          }
        })
        currentExhibitions.sort((a, b) => {
          if (dayjs(a.start.date).isAfter(dayjs(b.start.date))) {
            return -1
          } else {
            return 1
          }
        })
        if (currentExhibitions.length) {
          return currentExhibitions
        } else {
          return
        }
      } else {
        return false
      }
    },
    featuredExhibitions: (state) => {
      if (state.exhibitions) {
        let featuredExhibitions = state.exhibitions.filter((exh) => {
          if (exh.type) {
            return exh.type.some((t) => {
              return t.name === "Featured"
            })
          } else {
            return false
          }
        })

        return sortExhibitions(featuredExhibitions)
      } else {
        return false
      }
    },
    otherExhibitions: (state) => {
      if (state.exhibitions) {
        let otherExhibitions = state.exhibitions.filter((exh) => {
          if (exh.type) {
            return !exh.type.some((t) => {
              return t.name === "Featured"
            })
          } else {
            return true
          }
        })

        return sortExhibitions(otherExhibitions)
      } else {
        return false
      }
    },
    // Events - Current
    currentEvents: (state) => {
      if (state.events) {
        let now = dayjs(new Date())
        let currentEvents = state.events.filter((event) => {
          if (event.type) {
            return dayjs(event.end.date).isAfter(now)
          }
        })
        currentEvents.sort((b, a) => {
          if (dayjs(a.start.date).isAfter(dayjs(b.start.date))) {
            return -1
          } else {
            return 1
          }
        })
        return currentEvents
      } else {
        return false
      }
    },
    // Events - Other
    otherEvents: (state) => {
      if (state.events) {
        let now = dayjs(new Date())
        let otherEvents = state.events.filter((event) => {
          if (event.end.date) {
            return dayjs(event.end.date).isBefore(now)
          } else {
            return dayjs(event.start.date).isBefore(now)
          }
        })
        otherEvents.sort((a, b) => {
          if (dayjs(a.start.date).isAfter(dayjs(b.start.date))) {
            return -1
          } else {
            return 1
          }
        })
        return otherEvents
      } else {
        return false
      }
    },
    featuredPublications: (state) => {
      if (state.publications) {
        let featuredPublications = state.publications.filter((publication) => {
          return publication.acf.featured
        })
        return sortPublications(featuredPublications)
      } else {
        return false
      }
    },
    otherPublications: (state) => {
      if (state.publications) {
        let otherPublications = state.publications.filter((publication) => {
          return !publication.acf.featured
        })
        return sortPublications(otherPublications)
      } else {
        return false
      }
    },
    getPublicationBySlug: (state) => (slug) => {
      return state.publications.find((pub) => pub.slug === slug)
    },
    getInvestigationBySlug: (state) => (slug) => {
      return state.investigations
        .concat(state.studies)
        .find((inv) => inv.slug === slug)
    },
    getExhibitionBySlug: (state) => (slug) => {
      return state.exhibitions.find((exh) => exh.slug === slug)
    },
    investigationCategories: (state) => {
      if (state.investigations) {
        const items = state.investigations
          .map((x) => x.investigation_area)
          .flat(1)
        return uniqBy(items, (x) => x.term_id)
      } else {
        return []
      }
    },
    investigationMethodologies: (state) => {
      if (state.investigations) {
        const items = state.investigations.map((x) => x.methodologies).flat(1)
        return uniqBy(items, (x) => x.ID)
      } else {
        return []
      }
    },
  },
})
